import clsx from 'clsx'

/* blocks */
import SetServiceContext from '@/templates/SetServiceContext/SetServiceContext'
import PageHero from '@/cmp/organism/PageHero/PageHero'
import CustomerLogo from '@/cmp/organism/CustomerLogo/CustomerLogo'
import PlatformBenefits from '@/cmp/organism/PlatformBenefits/PlatformBenefits'
import WhyPepper from './components/WhyPepper/WhyPepper'
import CategoryTypes from './components/CategoryTypes/CategoryTypes'
import Samples from './components/Samples/Samples'
import Features from '@/cmp/organism/Feature/Features'
import Statistics from '../ContentTypes/components/Statistics/Statistics'
import SignUp from './components/SignUp/SignUp'
import FAQNew from '@/cmp/organism/FAQNew/FAQNew'

/* styles */
import styles from './_.module.css'

interface Props {
  data: NewServicesDetailsPageAttributes
}

function IndividualServices({ data }: Props): JSX.Element {
  return (
    <>
      <SetServiceContext
        name={data.pageTitle}
        type={data.type}
        slug={data.slug}
      />

      <PageHero
        layout="left"
        class={clsx(styles.description, styles.heroContainer)}
        primaryHeadline={data.hero.title}
        subText={data.hero.description}
        primaryCta={data.hero.primaryCta}
        secondaryCta={data.hero.secondaryCta}
        footNote={data.hero.footnote}
        image={data.hero.image?.data?.attributes}
      />

      {data.brands && data.brands.brands.data.length > 0 ? (
        <CustomerLogo
          title={data.brands.title}
          brands={data.brands.brands.data.map(i => ({
            ...i.attributes.logo.data.attributes,
            alternativeText: i.attributes.name
          }))}
        />
      ) : null}

      {data.platformBenefitList.length > 0 ? (
        <PlatformBenefits
          title={data.platformBenefitTitle}
          description={data.platformBenefitDescription}
          list={data.platformBenefitList.map(item => ({
            title: item.title ?? '',
            description: item.description ?? '',
            image: item.multimedia?.media?.data?.attributes
          }))}
        />
      ) : null}

      {data.whyPepper?.title ? (
        <WhyPepper
          class={clsx(styles.description)}
          primaryHeadline={data.whyPepper.title}
          subText={data.whyPepper.description}
          primaryCta={data.whyPepper.primaryCta}
          image={data.whyPepper.image?.data?.attributes}
        />
      ) : null}

      {data.categoryTypesList.length > 0 ? (
        <CategoryTypes
          title={data.categoryTypesTitle}
          services={data.categoryTypesList}
        />
      ) : null}

      {data.samplesList.length > 0 ? (
        <Samples
          title={data.samplesTitle}
          sampleList={data.samplesList.map(i => ({
            id: i.id,
            link: i.link,
            bgColor: i.color,
            title: i.title ?? '',
            description: i.description ?? '',
            image: i.multimedia?.media?.data?.attributes,
            colorOne: i.additionalFields?.colorOne as string | undefined,
            colorTwo: i.additionalFields?.colorTwo as string | undefined
          }))}
        />
      ) : null}

      {data.orderProcessSteps.length > 0 ? (
        <Features
          title={data.orderProcessTitle}
          class={styles.featureContainer}
          list={data.orderProcessSteps.map(i => ({
            id: i.id,
            link: i.link,
            bgColor: i.color,
            title: i.title ?? '',
            description: i.description ?? '',
            image: i.multimedia?.media?.data?.attributes,
            tag: i.additionalFields?.tag as string | null
          }))}
          mode="light"
        />
      ) : null}

      {data.statisticsList.length > 0 ? (
        <Statistics
          title={data.statisticsTitle}
          statistics={data.statisticsList.map(i => ({
            stat: i.title ?? '',
            color: i.color,
            description: i.description ?? ''
          }))}
          isDark={true}
        />
      ) : null}

      {data.signUp?.title ? (
        <SignUp
          class={clsx(styles.signUpContainer)}
          primaryHeadline={data.signUp.title}
          subText={data.signUp.description}
          tag={data.signUp.footnote ?? ''}
          primaryCta={data.signUp.primaryCta}
          image={data.signUp.image?.data?.attributes}
          bgColor="#1700EB"
        />
      ) : null}

      {data.faqList.length > 0 ? (
        <FAQNew
          title={data.faqTitle}
          list={data.faqList.map(i => ({
            id: i.id,
            question: i.question ?? '',
            answer: i.answer ?? ''
          }))}
        />
      ) : null}
    </>
  )
}

export default IndividualServices
